export const EXPEDITION_TRACK_ROUTE_SOURCE = 'expedition-track-route';
export const EXPEDITION_TRACK_STOPS_SOURCE = 'expedition-track-stops';
export const EXPEDITION_TRACK_LINE_LAYER = 'expedition-track-line';
export const EXPEDITION_TRACK_ARROWS_LAYER = 'expedition-track-arrows';
export const EXPEDITION_TRACK_STOPS_LAYER = 'expedition-track-stops';
export const EXPEDITION_TRACK_LABELS_LAYER = 'expedition-track-labels';
export const EXPEDITION_ARROW_IMAGE = 'expedition-arrow';

const EMPTY_FC = () => ({ type: 'FeatureCollection', features: [] });

const EXPEDITION_TRACK_COLOR = '#0ea5e9';

const ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="${EXPEDITION_TRACK_COLOR}" d="M4 12h14l-4-4 1.4-1.4L21.8 12l-6.4 6.4L14 17l4-4H4z"/></svg>`;
const ARROW_IMAGE_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(ARROW_SVG)}`;

const loadImageElement = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = src;
});

export const splitExpeditionTrackGeoJson = (geojson) => {
  if (!geojson || !Array.isArray(geojson.features)) {
    return { route: EMPTY_FC(), stops: EMPTY_FC() };
  }

  const routeFeatures = geojson.features.filter(
    (feature) => feature?.properties?.type === 'route',
  );
  const stopFeatures = geojson.features.filter(
    (feature) => feature?.properties?.type === 'stop',
  );

  return {
    route: { type: 'FeatureCollection', features: routeFeatures },
    stops: { type: 'FeatureCollection', features: stopFeatures },
  };
};

export const hasExpeditionTrackData = (geojson) => {
  const { route, stops } = splitExpeditionTrackGeoJson(geojson);
  return route.features.length > 0 || stops.features.length > 0;
};

export const getExpeditionTrackBounds = (geojson) => {
  const { route, stops } = splitExpeditionTrackGeoJson(geojson);
  const coordinates = [];

  for (const feature of route.features) {
    if (feature.geometry?.type === 'LineString' && Array.isArray(feature.geometry.coordinates)) {
      coordinates.push(...feature.geometry.coordinates);
    }
  }
  for (const feature of stops.features) {
    if (feature.geometry?.type === 'Point' && Array.isArray(feature.geometry.coordinates)) {
      coordinates.push(feature.geometry.coordinates);
    }
  }

  if (!coordinates.length) return null;

  let minX = coordinates[0][0];
  let minY = coordinates[0][1];
  let maxX = coordinates[0][0];
  let maxY = coordinates[0][1];

  for (const [x, y] of coordinates) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  return [
    [minX, minY],
    [maxX, maxY],
  ];
};

export const addExpeditionTrackSourcesAndLayers = (sources, layers) => {
  sources[EXPEDITION_TRACK_ROUTE_SOURCE] = {
    type: 'geojson',
    data: EMPTY_FC(),
  };
  sources[EXPEDITION_TRACK_STOPS_SOURCE] = {
    type: 'geojson',
    data: EMPTY_FC(),
  };

  layers.push(
    {
      id: EXPEDITION_TRACK_LINE_LAYER,
      type: 'line',
      source: EXPEDITION_TRACK_ROUTE_SOURCE,
      layout: {
        visibility: 'none',
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': EXPEDITION_TRACK_COLOR,
        'line-width': 3,
        'line-opacity': 0.9,
      },
    },
    {
      id: EXPEDITION_TRACK_ARROWS_LAYER,
      type: 'symbol',
      source: EXPEDITION_TRACK_ROUTE_SOURCE,
      layout: {
        visibility: 'none',
        'symbol-placement': 'line',
        'symbol-spacing': 100,
        'icon-image': EXPEDITION_ARROW_IMAGE,
        'icon-size': 1.1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    },
    {
      id: EXPEDITION_TRACK_STOPS_LAYER,
      type: 'circle',
      source: EXPEDITION_TRACK_STOPS_SOURCE,
      layout: { visibility: 'none' },
      paint: {
        'circle-radius': 6,
        'circle-color': '#f97316',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.95,
      },
    },
    {
      id: EXPEDITION_TRACK_LABELS_LAYER,
      type: 'symbol',
      source: EXPEDITION_TRACK_STOPS_SOURCE,
      layout: {
        visibility: 'none',
        'text-field': [
          'format',
          ['get', 'name'],
          { 'font-scale': 1 },
          '\n',
          {},
          ['get', 'date'],
          { 'font-scale': 0.85 },
        ],
        'text-font': ['Open Sans Regular'],
        'text-size': 12,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': '#f97316',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    },
  );
};

export const ensureExpeditionArrowImage = async (map) => {
  if (map.hasImage(EXPEDITION_ARROW_IMAGE)) return;
  const image = await loadImageElement(ARROW_IMAGE_URL);
  if (!map.hasImage(EXPEDITION_ARROW_IMAGE)) {
    map.addImage(EXPEDITION_ARROW_IMAGE, image);
  }
};

export const applyExpeditionTrackData = (map, geojson) => {
  const routeSource = map.getSource(EXPEDITION_TRACK_ROUTE_SOURCE);
  const stopsSource = map.getSource(EXPEDITION_TRACK_STOPS_SOURCE);
  if (!routeSource?.setData || !stopsSource?.setData) return false;

  const { route, stops } = splitExpeditionTrackGeoJson(geojson);
  routeSource.setData(route);
  stopsSource.setData(stops);
  return true;
};

export const applyExpeditionTrackVisibility = (map, visible) => {
  const visibility = visible ? 'visible' : 'none';
  [
    EXPEDITION_TRACK_LINE_LAYER,
    EXPEDITION_TRACK_ARROWS_LAYER,
    EXPEDITION_TRACK_STOPS_LAYER,
    EXPEDITION_TRACK_LABELS_LAYER,
  ].forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  });
};
